/* eslint-disable default-case */
import {
  ComponentDescription,
  ComponentEventDefinition,
  ComponentEventDefinitions,
  ComponentInstance,
  ComponentInstancePropertyBindings,
  ComponentPropertyDefinitions,
  StyleBindings,
  StyleClassDefinitionsByComponentId,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import migrateSchema from '../composer/schema/utils/migrateSchema';
import { getBlankBindingForSchema, convertBindingToUiComponentPropertyFormat } from './bindings';
import isBindingTypeReadableFromOutside from '../composer/bindings/utils/isBindingTypeReadableFromOutside';
import isPrivateBindingType from '../composer/bindings/utils/isPrivateBindingType';
import { pickCompositeRootInnerStyles, pickCompositeRootOuterStyles } from './styles';
import ComponentInstanceProxy from '../composer/dolanruntime/ComponentInstanceProxy';
import getNestedStyleOverridesQueryKey from '../composer/styles/utils/getNestedStyleOverridesQueryKey';
import traverseComponent from '../composer/components/utils/traverseComponent';
import isBindingTypeWriteableFromOutside from '../composer/bindings/utils/isBindingTypeWriteableFromOutside';

/**
 * Returns whether or not the given component instance can contain children.
 * @param {object} component component definition object
 * @param {object} description component description (by default read from component)
 */
export function canComponentHaveChildren(
  component: ComponentInstanceProxy,
  description: ComponentDescription = component.description,
): boolean {
  const { name } = component;
  switch (name) {
    case 'mobile_tabbed_web_sidepanel_layout':
    case 'responsive_column_layout':
    case 'fixed_column_layout':
    case 'layout':
    case 'layout_repeat':
    case 'vertical_box_layout':
      return true;
  }
  if (!description) {
    return false;
  }
  return (
    !!description.container ||
    description.reactnative_class === 'FlexBoxLayout' ||
    description.reactnative_class === 'ConditionalRenderer' ||
    description.reactnative_class === 'Recycler'
  );
}

/**
 * Returns "vertical", "horizontal" or null according to the layout flow direction of
 * the given component, determined by its style. If the component cannot have children,
 * then returns null.
 * @param {object} component component definition object
 * @param {object} description component description (by default read from component)
 */
export function getComponentLayoutDirection(
  component: ComponentInstanceProxy,
  description: ComponentDescription = component.description,
) {
  if (!canComponentHaveChildren(component, description)) {
    // Cannot have children -> does not have a layout flow direction
    return null;
  }
  const { name } = component;
  switch (name) {
    case 'fixed_column_layout':
    case 'layout':
    case 'layout_repeat':
      // A special horizontal layout component, e.g. Row or some legacy thing
      return 'horizontal';
  }
  const { display, flexDirection } = component.style || {};
  if (display != null && display !== 'flex') {
    // Display is none or some unknown value
    return 'vertical';
  }
  if (flexDirection === 'row' || flexDirection === 'row-reverse') {
    // Direction explicitly set to horizontal by flex direction style
    return 'horizontal';
  }
  // Otherwise default to vertical layout
  return 'vertical';
}

/**
 * Traverses the component tree upwards starting from the current component
 * and returns the closest component that matches the criteria.
 * The callback function should return a truthy value when matches.
 * @param {object|null} [component] component instance to start searching
 * @param {function} fn callback for whether the component matches
 * @returns {*}
 */
export const findClosestComponent = (
  component: ComponentInstanceProxy | null | undefined,
  fn: (parent: ComponentInstanceProxy) => boolean | void,
) => {
  // Finds the first component matching the given criteria by trying first the
  // component itself, and then traversing up the parent tree.
  while (component) {
    if (fn(component)) {
      return component;
    }
    // eslint-disable-next-line no-param-reassign
    component = component.$parent;
  }
  return null;
};

/**
 * Traverses the component tree upwards starting from the current component
 * and returns the closest composite component instance, or null if not found.
 * @param {object} component current component instance
 */
export function findCompositeRootComponent(component: ComponentInstanceProxy | ComponentInstance | null | undefined) {
  if (!(component instanceof ComponentInstanceProxy)) return null;
  return findClosestComponent(component, (parent) => {
    const { description } = parent;
    return description != null && !!description.composite_json;
  });
}

export const getIsolatedComponent = (component: ComponentInstanceProxy | ComponentInstance | null | undefined) => {
  if (!component || !(component instanceof ComponentInstanceProxy)) {
    return null;
  }
  const closestComposite = findCompositeRootComponent(component);
  if (component?.description?.composite_json) {
    return component;
  }
  if (closestComposite?.description?.composite_json) {
    return closestComposite;
  }
  return null;
};

/**
 * Generates an object of component property bindings that can be set to the `args`
 * property of a component instance. Takes an array of property definitions from
 * component description `args`.
 *
 * @param {array} argDefinitions list of property definitions
 * @param {array} oldBindings optional bindings to use for the new bindigns
 */
export function getInitialComponentPropertyBindings(
  argDefinitions: ComponentPropertyDefinitions,
  oldBindings?: ComponentInstancePropertyBindings,
): ComponentInstancePropertyBindings {
  const inputArgs: ComponentInstancePropertyBindings = {};
  argDefinitions.forEach((argDef) => {
    const { name, bindingType } = argDef;
    if (typeof name !== 'string') return;
    // Attempt to use existing binding
    const oldBinding = oldBindings?.[name];
    if (oldBinding != null && isBindingTypeWriteableFromOutside(bindingType)) {
      inputArgs[name] = oldBinding;
      return;
    }
    // Skip creating bindings for outbound, private or two-way bindings
    const skip = isBindingTypeReadableFromOutside(bindingType) || isPrivateBindingType(bindingType);
    if (bindingType == null || !skip) {
      // Create a blank binding
      const schema = migrateSchema(argDef);
      const binding = getBlankBindingForSchema(schema, undefined /* TODO: Would this be useful? */, bindingType);
      if (binding != null) {
        inputArgs[name] = convertBindingToUiComponentPropertyFormat(binding);
      }
    }
  });
  return inputArgs;
}

const emptyEvents = {};

export const fallbackEventObjectSchema = {
  type: 'object',
  title: 'Event object',
  description: 'Object containing information about the triggered event.',
  additionalProperties: { type: 'value' },
};

export const fallbackEventValueSchemas = {
  eventValue: {
    title: 'Event',
    description: 'Value of the occurred event, depending on the event type.',
  },
  event: fallbackEventObjectSchema,
};

export function migrateComponentEvents(
  events: ComponentEventDefinitions | null | undefined,
): Record<string, ComponentEventDefinition> {
  if (Array.isArray(events)) {
    // Migrate from the legacy event format
    const migratedEvents: Record<string, ComponentEventDefinition> = {};
    events.forEach((eventName) => {
      migratedEvents[eventName] = {
        title: `Component ${eventName}`,
        type: 'object',
        properties: fallbackEventValueSchemas,
      };
    });
    return migratedEvents;
  }
  if (!events) {
    return emptyEvents;
  }
  Object.keys(events).forEach((eventName) => {
    const event = (events as any)[eventName];
    if (event && !event.properties && event.valueSchema) {
      // eslint-disable-next-line no-param-reassign
      events = {
        ...events,
        [eventName]: {
          title: event.title || `Component ${eventName}`,
          type: 'object',
          properties: {
            eventValue: event.valueSchema,
            event: fallbackEventObjectSchema,
          },
        },
      };
    }
  });
  return events;
}

/**
 * Finds relative path of sub component to a root component
 * compatible with lodash get set utils,
 * returns null if root does not contain sub component
 */
function findSubComponentPath(subComponent: ComponentInstanceProxy, rootComponent: ComponentInstanceProxy) {
  let path: (string | number)[] = [];
  let current = subComponent;
  while (current !== rootComponent) {
    path = ['children', current.$parent!.children.indexOf(current), ...path];
    current = current.$parent!;
    if (!current) {
      return null;
    }
  }
  return path;
}

/**
 * Calls the given function for each component in the component tree, including path
 * compatible with lodash get set utils
 * If the function returns exactly value `false`, then the iteration stops.
 */
export function traverseComponentWithPath(
  rootComponent: ComponentInstanceProxy,
  fn: (descendant: ComponentInstanceProxy, depth: number, path: (string | number)[]) => void,
) {
  traverseComponent(rootComponent, (currentComponent, depth) => {
    fn(currentComponent, depth, findSubComponentPath(currentComponent, rootComponent) as (string | number)[]);
  });
}

/**
 * Returns the key to be used in the nested_overrides object structure for composite
 * sub components. Currently strips start of the id which changes across instances,
 * in the future can be used to return id of the component within the template
 */
export function getNestedOverridesKey(
  component: ComponentInstanceProxy | ComponentInstance | null | undefined,
): string | null {
  if (!component) return null;
  const rootComponent = findCompositeRootComponent(component);
  return getNestedStyleOverridesQueryKey(component, rootComponent === component);
}

export function getNestedOverridesForComponent({
  component,
  styleClasses,
  isolatedComponentId,
  isolationType,
  editedStyleClass,
}: {
  component: ComponentInstanceProxy | ComponentInstance;
  styleClasses?: StyleClassDefinitionsByComponentId;
  isolatedComponentId?: string | null;
  isolationType?: 'light' | 'full' | null;
  editedStyleClass?: string | null;
}) {
  const compositeRootComponent = findCompositeRootComponent(component);
  if (!compositeRootComponent) {
    return {};
  }
  const isFullIsolationMode = isolatedComponentId && isolationType === 'full';
  // TODO: should use getComponentActiveStyleClass to support nested composites?
  const compositeRootComponentStyleClass = isFullIsolationMode ? editedStyleClass : compositeRootComponent?.style_class;
  const nestedOverides =
    styleClasses?.[compositeRootComponent?.name]?.[compositeRootComponentStyleClass as string]?.nested_overrides || {};
  return nestedOverides[component?.id] || nestedOverides[getNestedOverridesKey(component) as string];
}

/**
 * Returns the component template style classes as an object
 * with component "names" as keys.
 */
export function getComponentTemplateStyleClasses(
  componentTemplates: ComponentDescription[] = [],
): StyleClassDefinitionsByComponentId {
  return componentTemplates.reduce<StyleClassDefinitionsByComponentId>(
    (result, componentTemplate) =>
      componentTemplate.default_style_classes
        ? {
            ...result,
            [componentTemplate.name]: componentTemplate.default_style_classes,
          }
        : result,
    {},
  );
}

/**
 * Returns the component's active style class, depending on the
 * nested_overrides of composite root and the editor state
 */
export function getComponentActiveStyleClass({
  component,
  styleClasses,
  ignoreNestedOverrides = false,
  isolatedComponentId,
  isolationType,
  editedStyleClass,
}: {
  component?: ComponentInstanceProxy | ComponentInstance | null;
  styleClasses?: StyleClassDefinitionsByComponentId;
  ignoreNestedOverrides?: boolean;
  isolatedComponentId?: string | null;
  isolationType?: 'light' | 'full' | null;
  editedStyleClass?: string | null;
}): string | null | undefined {
  if (!component) {
    return null;
  }
  const isFullIsolationMode = isolatedComponentId && isolationType !== 'light';
  const compositeRootComponent = findCompositeRootComponent(component);
  // TODO: should become a recursive call to support nested composites?
  const componentNestedOverrides =
    component && !ignoreNestedOverrides
      ? compositeRootComponent?.nested_overrides?.[getNestedOverridesKey(component) as string] || {}
      : {};
  const dolanNestedOverridesForComponent = !ignoreNestedOverrides
    ? getNestedOverridesForComponent({
        component,
        styleClasses,
        isolatedComponentId,
        isolationType,
        editedStyleClass,
      })
    : {};
  if (isFullIsolationMode) {
    return dolanNestedOverridesForComponent?.style_class || component?.style_class;
  }
  return (
    componentNestedOverrides?.style_class || dolanNestedOverridesForComponent?.style_class || component?.style_class
  );
}

/**
 * Returns the composite component direct children inline styles and style classes
 * as nested_overrides structure
 */
export function getInlineStylesAsNestedOverrides(compositeComponent: ComponentInstanceProxy) {
  const nestedOverides: Record<string, { style_class?: string | null; style: StyleBindings }> = {};
  traverseComponent(compositeComponent, (currentComponent) => {
    const currentCompositeRoot = findCompositeRootComponent(currentComponent);
    const isDirectChildren =
      currentCompositeRoot === compositeComponent ||
      findCompositeRootComponent(currentComponent.$parent) === compositeComponent;
    if (isDirectChildren) {
      nestedOverides[getNestedOverridesKey(currentComponent) as string] =
        compositeComponent !== currentComponent
          ? {
              style_class: currentComponent.style_class,
              style: currentComponent.style,
            }
          : {
              style: pickCompositeRootInnerStyles(currentComponent.style),
            };
    }
  });
  return nestedOverides;
}

/**
 * Clears the inline styles of the given composite's nested components
 */
export function clearTemplateInlineStyles(compositeComponent: ComponentInstanceProxy) {
  traverseComponent(compositeComponent, (currentComponent) => {
    const isCompositeComponent = !!currentComponent?.description.composite_json;
    const currentCompositeRoot = findCompositeRootComponent(currentComponent);
    if (currentComponent === compositeComponent) {
      // eslint-disable-next-line no-param-reassign
      currentComponent.style = pickCompositeRootOuterStyles(currentComponent.style);
      return;
    }
    if (!isCompositeComponent && currentCompositeRoot === compositeComponent) {
      // eslint-disable-next-line no-param-reassign
      currentComponent.style = {};
    }
  });
}

export function isContainer(component: ComponentInstance | null | undefined) {
  // TODO: expand this list
  return component != null && ['layout', 'vertical_box_layout', 'responsive_column_layout'].includes(component.name);
}
